import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the CandidateContext
const EmploymentDetailsContext = createContext();

// Custom hook to use the CandidateContext
export const useEmploymentDetails = () => useContext(EmploymentDetailsContext);

// Provider component
export const EmploymentDetailsProvider = ({ children }) => {
  const [employmentDetails, setEmploymentDetails] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 20,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const API_URL = "https://hrmsnew.prepstripe.com/api/v1/employment-details"; // Replace with your actual API URL
  const token = getToken(); // Retrieve the token

  // Fetch candidates from the API on load
  const fetchEmploymentDetails = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await new Promise((r) => setTimeout(r, 1000));
      setEmploymentDetails(response.data?.data?.data || []);
      setPagination({
        currentPage: response.data?.data?.current_page || 1,
        totalPages: response.data?.data?.last_page || 1,
        totalItems: response.data?.data?.total || 0,
        perPage: response.data?.data?.per_page || 20,
      });
    } catch (error) {
      console.error("Error fetching employment details:", error);
    }finally {
      setLoading(false);
    }
  };
  
  // Use the fetch function in useEffect
  useEffect(() => {
    fetchEmploymentDetails();
  }, [API_URL, token]);

  const addEmploymentDetails = async (newEmploymentDetail) => {
    try {
      const response = await axios.post(API_URL, newEmploymentDetail, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // setEmploymentDetails((prev) => [...prev, response.data?.data]);
      setEmploymentDetails((prev) => [response.data?.data, ...prev]);

    } catch (error) {
      console.error("Error adding employment details:", error);
      throw error.response?.data; // Pass errors back to the component
    }
  };

  // Update an existing candidate
  const updateEmploymentDetails = async (updatedEmploymentDetail) => {
    try {
      // console.log("updateEmploymentDetails called with:", updatedEmploymentDetail);
      const isFormData = updatedEmploymentDetail instanceof FormData;
      const headers = {
        Authorization: `Bearer ${token}`,
        ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
      };

      // console.log(`${API_URL}/${updatedEmploymentDetail.employee_id}`)
      // console.log("about to send the payload", updatedEmploymentDetail)
      const response = await axios.put(`${API_URL}/${updatedEmploymentDetail.employee_id}`, updatedEmploymentDetail, {
        headers,
      });

      // // console.log(response.data)

      fetchEmploymentDetails();

      // setEmploymentDetails((prevRequests) =>
      //   prevRequests.map((request) =>
      //     request.employee_id === updatedEmploymentDetail.employee_id ? { ...request, ...updatedEmploymentDetail } : request
      //   )
      // );



      setErrors({}); // Clear errors on successful update
      return response.data?.data; // Optionally return updated data
    } catch (error) {
      console.error("Error updating candidate:", error);
      const generalError = error.response?.data?.message || "Something went wrong.";
      setErrors({ general: generalError, ...(error.response?.data?.errors || {}) });
      throw error; // Optionally rethrow for higher-level handling
    }
  };

  // Delete a candidate
  const deleteEmploymentDetails = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setEmploymentDetails((prev) => prev.filter((employmentDetail) => employmentDetail.employment_id !== id));
    } catch (error) {
      console.error("Error deleting candidate:", error);
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      employmentDetails,
      pagination,
      loading,
      fetchEmploymentDetails,
      addEmploymentDetails,
      updateEmploymentDetails,
      deleteEmploymentDetails,
      errors,
    }),
    [employmentDetails, errors, loading] // Recalculate the value only when these dependencies change
  );

  return <EmploymentDetailsContext.Provider value={value}>{children}</EmploymentDetailsContext.Provider>;
};
